import React, { useState } from 'react'
import { Table } from 'shared/ui/Table/Table'
import { useWithAltTextColumns } from './columns'
import { Box } from '@mui/material'
import { useListSwap } from 'shared/hooks'
import { ConfirmCommitButton } from 'shared/ui'
import { getRemoveAltTextUpdates } from './utility'
import { doUpdate } from '../nodeUpdates/utility'
import { SelectedUrl } from './SelectedUrl'
import { altTextTreeAtom, byUrlDataAtom, sortedTreeUrlsAtom } from './atoms'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { InfoDisplay } from '../../utility/Containers'

// WithAltText - Displays all images that have alt text
// You can select any subset of these images to remove alt text.
// You can select any image to see more information about that image.
export function WithAltText() {
  const { withAltText } = useAtomValue(sortedTreeUrlsAtom)
  const { resetTree } = useAtomValue(altTextTreeAtom)
  const allUrlData = useAtomValue(byUrlDataAtom)
  const [selectedUrl, setSelectedUrl] = useState('')

  const {
    toInclude: toKeepAltText,
    toExclude: toRemoveAltText,
    onExclude: onToKeepAltText,
    onInclude: onToRemoveAltText,
  } = useListSwap({
    items: withAltText,
  })

  const { withColumns, toRemoveColumns } = useWithAltTextColumns({
    setSelectedUrl,
    onToRemoveAltText,
    onToKeepAltText,
  })

  const removeAltText = async () => {
    const updates = getRemoveAltTextUpdates(
      toRemoveAltText.map((url) => allUrlData[url]),
    )
    await doUpdate(...updates)
    resetTree?.()
  }

  const doUrlUpdate = async (_: any, url: string) => {
    await doUpdate(...getRemoveAltTextUpdates([allUrlData[url]]))
    resetTree?.()
  }

  return (
    <Box>
      <Info />
      <Box padding="10px">
        <Table<string> columns={withColumns} items={toKeepAltText} />
      </Box>
      <Box padding="10px">
        <ConfirmCommitButton onClick={removeAltText}>
          Remove {toRemoveAltText.length} Alt Texts
        </ConfirmCommitButton>
        <Table<string> columns={toRemoveColumns} items={toRemoveAltText} />
      </Box>
      <SelectedUrl
        url={selectedUrl}
        onDeselect={() => setSelectedUrl('')}
        nodeSelectFunc={doUrlUpdate}
        leafSelectFunc={doUrlUpdate}
      />
    </Box>
  )
}

function Info() {
  return (
    <InfoDisplay>
      <Box>
        This component presents to the user all images with alt text, along with
        the alt text.
      </Box>
      <Box>The user is able to remove the alt text using this component.</Box>
      <Box>
        To remove alt text first press the R button in the image row. The image
        will appear in the table below. Once you have all the images where you
        want to remove the alt text in the bottom table press the Remove button
        to remove the alt text.
      </Box>
      <Box>
        Selecting an image will give you more details about the image and where
        it is in the tree at the bottom of this page.
      </Box>
    </InfoDisplay>
  )
}
